import React from 'react';
import { css, cx } from '@emotion/css';
import type { AnalyticDimension } from '@/hooks/query/analyticAccounting';
import { DEFAULT_CATEGORY_COLOR, normalizeColor } from './palette';

const chipBase = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  max-width: 100%;
  box-sizing: border-box;
`;

const sizeMd = css`
  padding: 1px 9px 1px 8px;
  font-size: 11.5px;
`;

const sizeSm = css`
  padding: 0 7px 0 6px;
  font-size: 10.5px;
`;

const dotCss = css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
`;

const dotSm = css`
  width: 7px;
  height: 7px;
`;

const codeCss = css`
  font-weight: 700;
  opacity: 0.65;
  flex: 0 0 auto;
`;

const labelCss = css`
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Solid colour dot — reusable (e.g. as a Suggest input leftElement). */
export function ColorDot({
  color,
  size = 8,
}: {
  color?: string | null;
  size?: number;
}) {
  const c = normalizeColor(color) || DEFAULT_CATEGORY_COLOR;
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        background: c,
        flex: '0 0 auto',
      }}
    />
  );
}

interface CategoryChipProps {
  label: string;
  code?: string | null;
  color?: string | null;
  size?: 'sm' | 'md';
  /** Show the `[CODE]` prefix inside the chip. */
  showCode?: boolean;
  className?: string;
  title?: string;
}

/**
 * Colored pill for an analytic category — a solid colour dot (carrying the
 * category identity) + dark, always-readable label, on a subtle colour-tinted
 * background. Used everywhere a category is displayed (tables, filters, cells,
 * dropdowns). The dot approach keeps light hues (jaune, lime…) legible where
 * colored text would fail.
 */
export function CategoryChip({
  label,
  code,
  color,
  size = 'md',
  showCode = false,
  className,
  title,
}: CategoryChipProps) {
  const c = normalizeColor(color) || DEFAULT_CATEGORY_COLOR;
  return (
    <span
      className={cx(chipBase, size === 'sm' ? sizeSm : sizeMd, className)}
      style={{
        background: `${c}1A`,
        border: `1px solid ${c}40`,
        color: 'var(--color-datatable-text, #1f2937)',
      }}
      title={title ?? (code ? `[${code}] ${label}` : label)}
    >
      <span className={cx(dotCss, size === 'sm' && dotSm)} style={{ background: c }} />
      {showCode && code ? <span className={codeCss}>{code}</span> : null}
      <span className={labelCss}>{label}</span>
    </span>
  );
}

/** Convenience: render a chip straight from an AnalyticDimension. */
export function DimensionChip({
  dim,
  size = 'md',
  showCode = false,
}: {
  dim?: AnalyticDimension | null;
  size?: 'sm' | 'md';
  showCode?: boolean;
}) {
  if (!dim) return null;
  return (
    <CategoryChip
      label={dim.label}
      code={dim.code}
      color={dim.color}
      size={size}
      showCode={showCode}
    />
  );
}
